import React from 'react';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

/**
 * Universal AppTimePicker component matching the style of AppDatePicker.
 * Accepts strings like "07:00 AM", "7:30 PM", "13:45", or Dayjs objects.
 * Outputs formatted 12-hour string "hh:mm A" (e.g. "07:00 AM").
 */
export default function AppTimePicker({
    label,
    placeholder = 'Select Time',
    value = '',
    onChange,
    disabled = false,
    size = 'small',
    fullWidth = true,
    className = '',
    sx = {},
    showPresets = false
}) {
    // Parse whatever string is passed into a valid Dayjs object
    const parseTimeToDayjs = (val) => {
        if (!val) return null;
        if (dayjs.isDayjs(val)) return val.isValid() ? val : null;

        const str = String(val).trim();
        if (!str) return null;

        // Try standard 12-hour with AM/PM
        const formats = ['h:mm A', 'hh:mm A', 'h:mm a', 'hh:mm a', 'HH:mm', 'H:mm', 'h A', 'hh A'];
        for (const fmt of formats) {
            const parsed = dayjs(`2000-01-01 ${str}`, `YYYY-MM-DD ${fmt}`);
            if (parsed.isValid()) return parsed;
        }

        const fallback = dayjs(`2000-01-01 ${str}`);
        return fallback.isValid() ? fallback : null;
    };

    const parsedValue = parseTimeToDayjs(value);

    const handleChange = (newValue) => {
        if (!newValue || !newValue.isValid()) {
            if (onChange) onChange('', { target: { value: '' } });
            return;
        }
        const formatted = newValue.format('hh:mm A');
        if (onChange) {
            onChange(formatted, { target: { value: formatted } });
        }
    };

    const commonPresets = [
        '06:30 AM', '07:00 AM', '08:00 AM', '08:30 AM',
        '11:00 AM', '01:00 PM', '04:30 PM', '05:00 PM',
        '07:30 PM', '08:00 PM'
    ];

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div className="app-timepicker-wrapper" style={{ width: fullWidth ? '100%' : 'auto' }}>
                <TimePicker
                    {...(label ? { label } : {})}
                    value={parsedValue}
                    onChange={handleChange}
                    disabled={disabled}
                    ampm={true}
                    slotProps={{
                        textField: {
                            size,
                            fullWidth,
                            placeholder,
                            className: `bg-white ${className}`,
                            sx: {
                                backgroundColor: '#fff',
                                borderRadius: '4px',
                                '& .MuiOutlinedInput-root': {
                                    fontSize: size === 'small' ? '0.825rem' : '1rem',
                                    paddingRight: '6px'
                                },
                                '& .MuiInputBase-input': {
                                    padding: size === 'small' ? '7.5px 10px' : undefined
                                },
                                ...sx
                            }
                        }
                    }}
                />

                {showPresets && (
                    <div className="d-flex flex-wrap gap-1 mt-1">
                        {commonPresets.map((preset) => (
                            <button
                                key={preset}
                                type="button"
                                className={`btn btn-xs py-0 px-1 border small ${value === preset ? 'btn-success text-white' : 'btn-light text-muted'}`}
                                style={{ fontSize: '0.7rem' }}
                                onClick={() => onChange && onChange(preset, { target: { value: preset } })}
                            >
                                {preset}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </LocalizationProvider>
    );
}
